import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Mail as MailIcon,
  Language as WebsiteIcon,
  Campaign as CampaignIcon,
  Timeline as PipelineIcon,
  Public as PublicIcon,
  TravelExplore as ScrapingIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const drawerWidth = 200;
const collapsedWidth = 60;

const menuItems = [
  { text: 'Pipeline', icon: <PipelineIcon sx={{ width: 17, height: 17, color: 'inherit' }} />, path: '/pipeline' },
  { text: 'Database', icon: '/database.png', path: '/database' },
  { text: 'Outreach', icon: '/send.png', path: '/outreach' },
  { text: 'Web Search', icon: <PublicIcon sx={{ width: 17, height: 17, color: 'inherit' }} />, path: '/outreach/search' },
  { text: 'Saved Results', icon: <PublicIcon sx={{ width: 17, height: 17, color: 'inherit' }} />, path: '/outreach/saved-results' },
  { text: 'Scraping', icon: <ScrapingIcon sx={{ width: 17, height: 17, color: 'inherit' }} />, path: '/scraping' },
  { text: 'MailWhiz Import', icon: <MailIcon sx={{ width: 17, height: 17, color: 'inherit' }} />, path: '/mailwhiz-import' },
  { text: 'Website Finder', icon: <WebsiteIcon sx={{ width: 17, height: 17, color: 'inherit' }} />, path: '/website-finder' },
  { text: 'Email Campaigns', icon: <CampaignIcon sx={{ width: 17, height: 17, color: 'inherit' }} />, path: '/mailwhiz-campaigns' },
];

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(true);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null);

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleAccountMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAccountMenuAnchor(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleAccountMenuClose();
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: collapsed ? collapsedWidth : drawerWidth,
        flexShrink: 0,
        transition: 'width 0.3s ease',
        '& .MuiDrawer-paper': {
          width: collapsed ? collapsedWidth : drawerWidth,
          boxSizing: 'border-box',
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
        },
      }}
    >
      <Box 
        onClick={toggleCollapse}
        sx={{ 
          p: collapsed ? 2 : 0,
          pt: collapsed ? 2 : 0.5,
          pb: collapsed ? 2 : 0.5,
          px: collapsed ? 2 : 1,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: collapsed ? 'center' : 'center',
          gap: 1,
          position: 'relative',
          minHeight: collapsed ? '64px' : '100px',
          height: collapsed ? '64px' : '100px',
          mb: collapsed ? 0 : -2,
          cursor: 'pointer',
        }}>
        <Box
          component="img"
          src="/ems.png"
          alt="EMS"
          sx={{
            maxWidth: collapsed ? '45px' : '400px',
            width: 'auto',
            height: 'auto',
            maxHeight: collapsed ? '45px' : '120px',
            objectFit: 'contain',
            backgroundColor: 'transparent',
            display: 'block', 
            mx: collapsed ? 0 : 'auto',
            position: collapsed ? 'static' : 'absolute',
            top: collapsed ? 'auto' : '50%',
            left: collapsed ? 'auto' : '50%',
            transform: collapsed ? 'none' : 'translate(-50%, -50%)',
            pointerEvents: 'none',
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <List sx={{ pt: 0 }}>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: collapsed ? 'center' : 'initial',
                  px: 2.5,
                  position: 'relative',
                  color: location.pathname === item.path ? '#1976d2' : '#64748b',
                  backgroundColor: location.pathname === item.path ? '#f0f7ff' : 'transparent',
                  fontWeight: location.pathname === item.path ? 600 : 500,
                  '&:hover': {
                    backgroundColor: location.pathname === item.path ? '#f0f7ff' : '#f8fafc',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    backgroundColor: location.pathname === item.path ? '#1976d2' : 'transparent',
                    transition: 'all 0.2s ease-in-out',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: collapsed ? 'auto' : 3,
                    justifyContent: 'center',
                  }}
                >
                  {typeof item.icon === 'string' ? (
                    <Box
                      component="img"
                      src={item.icon}
                      alt={item.text}
                      sx={{
                        width: 17,
                        height: 17,
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                {!collapsed && <ListItemText primary={item.text} />}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        
        <Box sx={{ mt: 'auto' }}>
          <List>
            {/* Settings - All users can access (viewers can edit profile) */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => navigate('/settings')}
                sx={{
                  minHeight: 48,
                  justifyContent: collapsed ? 'center' : 'initial',
                  px: 2.5,
                  position: 'relative',
                  color: location.pathname === '/settings' ? '#1976d2' : '#64748b',
                  backgroundColor: location.pathname === '/settings' ? '#f0f7ff' : 'transparent',
                  fontWeight: location.pathname === '/settings' ? 600 : 500,
                  '&:hover': {
                    backgroundColor: location.pathname === '/settings' ? '#f0f7ff' : '#f8fafc',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    backgroundColor: location.pathname === '/settings' ? '#1976d2' : 'transparent',
                    transition: 'all 0.2s ease-in-out',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: collapsed ? 'auto' : 3,
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    component="img"
                    src="/setting.png"
                    alt="Settings"
                    sx={{
                      width: 19,
                      height: 19,
                      objectFit: 'contain',
                    }}
                  />
                </ListItemIcon>
                {!collapsed && <ListItemText primary="Settings" />}
              </ListItemButton>
            </ListItem>

            {/* Account - shows menu with user info and logout */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleAccountMenuOpen}
                sx={{
                  minHeight: 48,
                  justifyContent: collapsed ? 'center' : 'initial',
                  px: 2.5,
                  position: 'relative',
                  color: '#64748b',
                  backgroundColor: 'transparent',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: '#f8fafc',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: collapsed ? 'auto' : 3,
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    component="img"
                    src={`/${user?.profileImage || 'profile1-96X96.png'}`}
                    alt="Account"
                    sx={{
                      width: 22,
                      height: 22,
                      objectFit: 'contain',
                      borderRadius: '50%',
                    }}
                  />
                </ListItemIcon>
                {!collapsed && <ListItemText primary={user?.username || "Account"} />}
              </ListItemButton>
            </ListItem>
          </List>

          {/* Account Menu */}
          <Menu
            anchorEl={accountMenuAnchor}
            open={Boolean(accountMenuAnchor)}
            onClose={handleAccountMenuClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
              },
            }}
          >
            {user && (
              <>
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {user.firstName} {user.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {user.email}
                  </Typography>
                </Box>
                <Divider />
              </>
            )}
            <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
              <LogoutIcon sx={{ mr: 1.5, fontSize: 20 }} />
              <Typography variant="body2">Logout</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
